import type { FC } from 'react';
import { msg } from '@gonfalon/messages/msg';
import { Text } from '@launchpad-ui/components';

import { vars } from '../../../../../css/vars';
import type { BaseContentProps } from '../Content';

export interface BooleanContentProps extends BaseContentProps {
  /**
   * The boolean value to display.
   * If not provided, displays "Empty".
   */
  value?: boolean;

  /**
   * Text style variant
   * @default 'default'
   */
  variant?: 'strong' | 'default' | 'small';
}

/**
 * Boolean content component for use inside Cell.
 *
 * @example
 * ```tsx
 * // Regular boolean
 * <Cell kind="regular" size="default">
 *   <BooleanContent value={true} />
 * </Cell>
 *
 * // Strong boolean
 * <Cell kind="regular" size="default">
 *   <BooleanContent value={false} variant="strong" />
 * </Cell>
 *
 * // Small boolean
 * <Cell kind="regular" size="default">
 *   <BooleanContent value={true} variant="small" />
 * </Cell>
 *
 * // Empty state
 * <Cell kind="regular" size="default">
 *   <BooleanContent />
 * </Cell>
 * ```
 */
export const BooleanContent: FC<BooleanContentProps> = ({ value, className, variant = 'default' }) => {
  const getTextProps = () => {
    switch (variant) {
      case 'strong':
        return { size: 'medium' as const, bold: true };
      case 'small':
        return { size: 'small' as const, bold: false };
      case 'default':
      default:
        return { size: 'medium' as const, bold: false };
    }
  };

  const { size, bold } = getTextProps();
  const isEmpty = value == null;
  const displayText = isEmpty ? msg('observability.tableCell.empty') : value.toString();

  return (
    <Text
      size={size}
      bold={bold}
      elementType="span"
      className={className}
      maxLines={1}
      style={{
        whiteSpace: 'nowrap',
        // Empty state color
        color: isEmpty ? vars.theme.interactive.fill.secondary.content.onDisabled : undefined,
      }}
    >
      {displayText}
    </Text>
  );
};
